import React, { useMemo, useRef, useState } from 'react';
import { Archive, Bot, ChevronDown, ChevronRight, Eye, EyeOff, Inbox, Loader2, Mail, MessageSquare, MessagesSquare, Pin, Plus, Star, Tag as TagIcon, User, Users, Wrench } from 'lucide-react';
import type { Agent, Conversation } from '../../types';
import { useChatStore } from '../../stores/chatStore';
import type { AsyncJobConversationSummary } from '../../utils/asyncJobs';
import { cn, formatRelativeTime, normalizeAgentType, truncateText, ui } from '../../utils';
import AgentAvatar from '../Common/AgentAvatar';
import TagBadge from '../Common/TagBadge';
import SidebarSkeleton from '../Sidebar/SidebarSkeleton';
import { LoadingFallback } from '../Common/LoadingFallback';
import PullToRefreshIndicator from '../Common/PullToRefreshIndicator';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';

const TagManagerModal = React.lazy(() => import('./TagManagerModal').then((m) => ({ default: m.TagManagerModal })));

type AgentConversationSummary = {
  latestConversation: Conversation | null;
  isActive: boolean;
  totalUnread: number;
  conversationCount: number;
  activeAsyncJobCount: number;
  latestAsyncJobSummary: AsyncJobConversationSummary | null;
};

interface SidebarProps {
  agents: Agent[];
  conversations: Conversation[];
  currentConversationId: string | null;
  onAgentSelect: (agentId: string) => void;
  onNewConversation?: (agentId: string) => void;
  onConversationSelect: (conversationId: string) => void;
  onNewGroupChat?: () => void;
  isOpen: boolean;
  mobileOffsetClassName?: string;
  mobileHeightClassName?: string;
  className?: string;
}

function getAgentCategory(agent: Agent) {
  // D7-08: agent.type is normalized to lowercase at the API boundary.
  const type = normalizeAgentType(agent.type);

  if (type === 'personal') return 'personal';
  if (type === 'user') return 'user';
  if (type === 'public') return 'utilities';
  if (type === 'chatbot') return 'chatbot';
  return 'shared';
}

/**
 * Returns the raw unread message count for a conversation.
 * Used for aggregation (agent category badges) — does NOT inflate for isUnread.
 */
function getConversationUnreadCount(conversation: Conversation) {
  const unreadCount = (conversation as Conversation & { unread_count?: number }).unread_count;
  return Number(conversation.unreadCount ?? unreadCount ?? 0);
}

/**
 * Returns whether a conversation should show as unread in the sidebar.
 * AC-MU-3: unread indicator visible if isUnread === true OR unreadCount > 0.
 */
function isConversationVisuallyUnread(conversation: Conversation) {
  return conversation.isUnread === true || getConversationUnreadCount(conversation) > 0;
}

function comparePinnedConversations(left: Conversation, right: Conversation) {
  const leftPinOrder = left.pinOrder ?? Number.MAX_SAFE_INTEGER;
  const rightPinOrder = right.pinOrder ?? Number.MAX_SAFE_INTEGER;
  if (leftPinOrder !== rightPinOrder) {
    return leftPinOrder - rightPinOrder;
  }

  return new Date(right.lastActivity).getTime() - new Date(left.lastActivity).getTime();
}

function compareRecentConversations(left: Conversation, right: Conversation) {
  return new Date(right.lastActivity).getTime() - new Date(left.lastActivity).getTime();
}

type ActiveSidebarFilter = 'all' | 'pinned' | 'unread' | 'tags' | 'hidden' | null;

function compareSidebarConversations(left: Conversation, right: Conversation) {
  if (left.isPinned && right.isPinned) {
    return comparePinnedConversations(left, right);
  }

  if (left.isPinned) return -1;
  if (right.isPinned) return 1;

  return compareRecentConversations(left, right);
}

function isRecentActivity(timestamp?: string | null) {
  if (!timestamp) {
    return false;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return Date.now() - date.getTime() < 2 * 60 * 1000;
}

function formatConversationLastActivity(conversation: Conversation) {
  const timestamp = conversation.lastMessage?.createdAt || conversation.lastMessage?.timestamp || conversation.lastActivity;
  if (!timestamp) {
    return '';
  }

  return isRecentActivity(timestamp) ? 'Active now' : formatRelativeTime(timestamp);
}

function normalizeAgentStatus(status?: string | null) {
  const normalizedStatus = String(status || '').toLowerCase();
  if (normalizedStatus === 'degraded' || normalizedStatus === 'slow') return 'degraded';
  if (['online', 'healthy'].includes(normalizedStatus)) return 'online';
  return 'offline';
}

function getAgentStatusDotClass(status?: string | null) {
  const normalizedStatus = normalizeAgentStatus(status);
  if (normalizedStatus === 'degraded') return 'bg-amber-400';
  if (normalizedStatus === 'online') return 'bg-emerald-400';
  return 'bg-rose-500';
}

function stripPreviewText(value?: string | null) {
  if (!value) {
    return '';
  }

  return value
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/^```[\w-]*\n?/, '').replace(/```$/, '').trim())
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/_([^_\n]+)_/g, '$1')
    .replace(/^\s*([-*+]|\d+\.|>|#{1,6})\s+/gm, '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function Sidebar({
  agents,
  conversations,
  currentConversationId,
  onAgentSelect,
  onNewConversation,
  onConversationSelect,
  onNewGroupChat,
  isOpen,
  mobileOffsetClassName = 'top-[60px]',
  mobileHeightClassName = 'h-[calc(100%-60px)]',
  className,
}: SidebarProps) {
  const isLoadingAgents = useChatStore((state) => state.isLoadingAgents);
  const isLoadingConversations = useChatStore((state) => state.isLoadingConversations);
  const isLoadingTags = useChatStore((state) => state.isLoadingTags);
  const tags = useChatStore((state) => state.tags);
  const toggleConversationPinned = useChatStore((state) => state.toggleConversationPinned);
  const toggleConversationArchived = useChatStore((state) => state.toggleConversationArchived);
  const hideConversation = useChatStore((state) => state.hideConversation);
  const unhideConversation = useChatStore((state) => state.unhideConversation);
  const markConversationUnread = useChatStore((state) => state.markConversationUnread);
  const setShowHidden = useChatStore((state) => state.setShowHidden);
  const favoriteAgentIds = useChatStore((state) => state.favoriteAgentIds);
  const toggleFavoriteAgent = useChatStore((state) => state.toggleFavoriteAgent);
  const loadConversations = useChatStore((state) => state.loadConversations);
  const fetchAgentStatuses = useChatStore((state) => state.fetchAgentStatuses);
  const asyncJobSummaries = useChatStore((state) => state.asyncJobSummaries);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActiveSidebarFilter>('unread');
  const [selectedTagId, setSelectedTagId] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>(() => {
    try {
      if (typeof localStorage?.getItem !== 'function') {
        return {};
      }

      const stored = localStorage.getItem('ally_collapsed_categories');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const favoriteAgentIdSet = useMemo(() => new Set(favoriteAgentIds), [favoriteAgentIds]);

  const selectedConversationForTags = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) ?? null,
    [conversations, selectedConversationId],
  );

  const toggleCategory = (category: string) => {
    setCollapsedCategories((current) => {
      const next = { ...current, [category]: !current[category] };
      try {
        if (typeof localStorage?.setItem === 'function') {
          localStorage.setItem('ally_collapsed_categories', JSON.stringify(next));
        }
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const categorized = useMemo(() => {
    const personal: Agent[] = [];
    const shared: Agent[] = [];
    const user: Agent[] = [];
    const utilities: Agent[] = [];
    const chatbot: Agent[] = [];

    agents.forEach((agent) => {
      const category = getAgentCategory(agent);
      if (category === 'personal') personal.push(agent);
      else if (category === 'user') user.push(agent);
      else if (category === 'utilities') utilities.push(agent);
      else if (category === 'chatbot') chatbot.push(agent);
      else shared.push(agent);
    });

    const sortByName = (left: Agent, right: Agent) => (left.name || '').localeCompare(right.name || '');
    const removeFavorites = (items: Agent[]) => items.filter((agent) => !favoriteAgentIdSet.has(agent.id)).sort(sortByName);
    return {
      favorites: agents.filter((agent) => favoriteAgentIdSet.has(agent.id)).sort(sortByName),
      personal: removeFavorites(personal),
      shared: removeFavorites(shared),
      user: removeFavorites(user),
      utilities: removeFavorites(utilities),
      chatbot: removeFavorites(chatbot),
    };
  }, [agents, favoriteAgentIdSet]);

  const sortedConversations = useMemo(() => [...conversations].sort(compareSidebarConversations), [conversations]);
  const groupConversations = useMemo(
    () => sortedConversations.filter((conversation) => conversation.isGroup && !conversation.archivedAt && !conversation.isHidden),
    [sortedConversations],
  );
  const agentConversationSummaryMap = useMemo(() => {
    const summaryMap = new Map<string, AgentConversationSummary>();

    conversations.forEach((conversation) => {
      if (conversation.isGroup || !conversation.agentId) {
        return;
      }

      // P1-2: exclude hidden conversations from agent category badge aggregation
      if (conversation.isHidden) {
        return;
      }

      const current = summaryMap.get(conversation.agentId) ?? {
        latestConversation: null,
        isActive: false,
        totalUnread: 0,
        conversationCount: 0,
        activeAsyncJobCount: 0,
        latestAsyncJobSummary: null,
      };
      const asyncJobSummary = asyncJobSummaries[conversation.id] ?? null;

      current.conversationCount += 1;
      current.activeAsyncJobCount += asyncJobSummary?.activeJobCount ?? 0;

      if (!conversation.archivedAt) {
        current.totalUnread += getConversationUnreadCount(conversation);
      }

      if (
        asyncJobSummary
        && (!current.latestAsyncJobSummary || new Date(asyncJobSummary.updatedAt).getTime() > new Date(current.latestAsyncJobSummary.updatedAt).getTime())
      ) {
        current.latestAsyncJobSummary = asyncJobSummary;
      }

      if (!current.latestConversation || new Date(conversation.lastActivity).getTime() > new Date(current.latestConversation.lastActivity).getTime()) {
        current.latestConversation = conversation;
      }

      if (currentConversationId && conversation.id === currentConversationId) {
        current.isActive = true;
      }

      summaryMap.set(conversation.agentId, current);
    });

    return summaryMap;
  }, [asyncJobSummaries, conversations, currentConversationId]);

  const handleOpenTagManager = (conversation?: Conversation | null) => {
    setSelectedConversationId(conversation?.id ?? null);
    setIsTagManagerOpen(true);
  };

  const handleFilterToggle = (filter: Exclude<ActiveSidebarFilter, null>) => {
    setActiveFilter((current) => {
      const next = current === filter ? null : filter;
      // AC-HIDE-3: when entering/leaving hidden filter, toggle includeHidden and reload
      const needsHidden = next === 'hidden';
      const wasHidden = current === 'hidden';
      if (needsHidden !== wasHidden) {
        setShowHidden(needsHidden);
        // Trigger reload to fetch with/without includeHidden param
        setTimeout(() => void loadConversations(), 0);
      }
      return next;
    });
  };

  const handleTagFilterChange = (tagId: string) => {
    if (tagId === '__manage__') {
      handleOpenTagManager(null);
      return;
    }

    setSelectedTagId(tagId);
    setActiveFilter('tags');
  };

  const activeFilterLabel = useMemo(() => {
    switch (activeFilter) {
      case 'all':
        return 'Recent conversations';
      case 'pinned':
        return 'Pinned conversations';
      case 'unread':
        return 'Unread conversations';
      case 'tags':
        return 'Tagged conversations';
      case 'hidden':
        return 'Hidden conversations';
      default:
        return '';
    }
  }, [activeFilter]);

  const visibleConversations = useMemo(() => {
    // AC-HIDE-1: hidden conversations excluded from all sidebar views except 'hidden' filter
    const nonHidden = sortedConversations.filter((conversation) => !conversation.isHidden);
    switch (activeFilter) {
      case 'all':
        return nonHidden.filter((conversation) => !conversation.archivedAt);
      case 'pinned':
        return nonHidden.filter((conversation) => conversation.isPinned && !conversation.archivedAt);
      case 'unread':
        return nonHidden.filter((conversation) => !conversation.archivedAt && isConversationVisuallyUnread(conversation));
      case 'tags':
        return selectedTagId
          ? nonHidden.filter((conversation) => !conversation.archivedAt && (conversation.tags ?? []).some((tag) => tag.id === selectedTagId))
          : [];
      case 'hidden':
        // AC-HIDE-3: show hidden conversations so users can find and unhide them
        return sortedConversations.filter((conversation) => conversation.isHidden);
      default:
        return [];
    }
  }, [activeFilter, selectedTagId, sortedConversations]);

  const emptyConversationText = useMemo(() => {
    switch (activeFilter) {
      case 'all':
        return 'No recent conversations yet.';
      case 'pinned':
        return 'Nothing pinned yet.';
      case 'unread':
        return 'No unread conversations right now.';
      case 'tags':
        return selectedTagId
          ? 'No conversations match this tag.'
          : (tags.length > 0 ? 'Pick a tag to filter conversations.' : 'Create a tag to start organizing conversations.');
      case 'hidden':
        return 'No hidden conversations. Hidden group chats appear here. Only you see your hidden list.';
      default:
        return '';
    }
  }, [activeFilter, selectedTagId, tags.length]);

  const showConversationResults = activeFilter !== null;

  // D7-06 (FE-6.5): gesture logic lives in the shared usePullToRefresh hook
  // (also used by MessageList for D5-14).
  const { pullDistance, isRefreshing, willRefresh, touchHandlers } = usePullToRefresh({
    scrollRef: scrollContainerRef,
    enabled: showConversationResults,
    onRefresh: () => Promise.all([loadConversations(), fetchAgentStatuses()]),
  });

  return (
    <>
      <div
        id="conversation-sidebar"
        tabIndex={-1}
        role="complementary"
        aria-label="Conversation and agent sidebar"
        className={cn(
          'fixed bottom-0 left-0 z-40 flex w-[min(22rem,100vw-1rem)] max-w-full flex-col overflow-hidden border-r border-border bg-card shadow-2xl shadow-black/30 transition-transform duration-300 lg:static lg:h-full lg:w-80 lg:translate-x-0 lg:shadow-none',
          mobileOffsetClassName,
          mobileHeightClassName,
          isOpen ? 'translate-x-0' : '-translate-x-full',
          className,
        )}
      >
        <div className="border-b border-border px-4 py-2">
          <div className="flex items-center gap-2">
            <FilterSegmentButton
              label="Unread conversations"
              active={activeFilter === 'unread'}
              onClick={() => handleFilterToggle('unread')}
            >
              <Inbox className="h-3.5 w-3.5" />
            </FilterSegmentButton>
            <FilterSegmentButton
              label="Recent conversations"
              active={activeFilter === 'all'}
              onClick={() => handleFilterToggle('all')}
            >
              <MessageSquare className="h-3.5 w-3.5" />
            </FilterSegmentButton>
            <FilterSegmentButton
              label="Pinned conversations"
              active={activeFilter === 'pinned'}
              onClick={() => handleFilterToggle('pinned')}
            >
              <Pin className="h-3.5 w-3.5" />
            </FilterSegmentButton>
            <FilterSegmentButton
              label="Tagged conversations"
              active={activeFilter === 'tags'}
              onClick={() => handleFilterToggle('tags')}
            >
              <TagIcon className="h-3.5 w-3.5" />
            </FilterSegmentButton>
            <FilterSegmentButton
              label="Hidden conversations"
              active={activeFilter === 'hidden'}
              onClick={() => handleFilterToggle('hidden')}
            >
              <EyeOff className="h-3.5 w-3.5" />
            </FilterSegmentButton>

            <div className="ml-auto flex items-center gap-2">
              {(isLoadingAgents || isLoadingTags) && agents.length === 0 ? (
                <div className="ally-spinner-ring h-4 w-4" />
              ) : null}
              {onNewGroupChat ? (
                <button
                  type="button"
                  onClick={onNewGroupChat}
                  className={cn(ui.button.icon, 'h-8 w-8 shrink-0 rounded-lg bg-blue-600 text-white hover:bg-blue-500 hover:text-white')}
                  aria-label="Start a new group chat"
                  title="Start a new group chat"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </div>

          {activeFilter === 'tags' ? (
            <select
              value={selectedTagId}
              onChange={(event) => handleTagFilterChange(event.target.value)}
              className={cn(ui.input, 'mt-3 px-3 py-2 text-xs')}
              aria-label="Filter conversations by tag"
            >
              <option value="">Pick tag…</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
              <option value="__manage__">Manage tags…</option>
            </select>
          ) : null}
        </div>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-3 pb-4 pt-3"
          {...touchHandlers}
        >
          {showConversationResults ? (
            <PullToRefreshIndicator isRefreshing={isRefreshing} pullDistance={pullDistance} willRefresh={willRefresh} />
          ) : null}
          {showConversationResults && isLoadingConversations && conversations.length === 0 ? (
            <SidebarSkeleton />
          ) : (
            <div className="space-y-4 opacity-100 transition-opacity duration-150">
              {showConversationResults ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between px-1">
                    <button
                      type="button"
                      onClick={() => setActiveFilter(null)}
                      className="text-xs font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground"
                    >
                      ← Back to agents
                    </button>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{activeFilterLabel}</span>
                  </div>

                  {visibleConversations.length > 0 ? (
                    <div className="space-y-2">
                      {visibleConversations.map((conversation) => (
                        <ConversationCard
                          key={conversation.id}
                          conversation={conversation}
                          asyncJobSummary={asyncJobSummaries[conversation.id] ?? null}
                          isActive={conversation.id === currentConversationId}
                          archived={false}
                          showingHidden={activeFilter === 'hidden'}
                          onSelect={onConversationSelect}
                          onTogglePinned={toggleConversationPinned}
                          onToggleArchived={toggleConversationArchived}
                          onHide={hideConversation}
                          onUnhide={unhideConversation}
                          onMarkUnread={markConversationUnread}
                          onManageTags={handleOpenTagManager}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={cn(ui.emptyState, 'px-4 py-8')}>
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-300">
                        <MessagesSquare className="h-5 w-5" />
                      </div>
                      <div className="text-sm font-medium text-foreground-bright">
                        {activeFilter === 'all' ? 'No conversations yet — start chatting!' : 'Nothing to show here yet'}
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground">{emptyConversationText}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {categorized.favorites.length > 0 ? (
                    <AgentSection
                      title="FAVORITES"
                      icon={<Star className="h-3 w-3 fill-current" />}
                      agents={categorized.favorites}
                      agentConversationSummaryMap={agentConversationSummaryMap}
                      isCollapsed={false}
                      collapsible={false}
                      onToggleCollapse={() => undefined}
                      onAgentSelect={onAgentSelect}
                      onNewConversation={onNewConversation}
                      favoriteAgentIds={favoriteAgentIdSet}
                      onToggleFavorite={toggleFavoriteAgent}
                    />
                  ) : null}

                  {categorized.personal.length > 0 ? (
                    <AgentSection
                      title="PERSONAL"
                      icon={<User className="h-3 w-3" />}
                      agents={categorized.personal}
                      agentConversationSummaryMap={agentConversationSummaryMap}
                      isCollapsed={collapsedCategories.PERSONAL || false}
                      onToggleCollapse={() => toggleCategory('PERSONAL')}
                      onAgentSelect={onAgentSelect}
                      onNewConversation={onNewConversation}
                      favoriteAgentIds={favoriteAgentIdSet}
                      onToggleFavorite={toggleFavoriteAgent}
                    />
                  ) : null}

                  {categorized.shared.length > 0 ? (
                    <AgentSection
                      title="SHARED"
                      icon={<Users className="h-3 w-3" />}
                      agents={categorized.shared}
                      agentConversationSummaryMap={agentConversationSummaryMap}
                      isCollapsed={collapsedCategories.SHARED || false}
                      onToggleCollapse={() => toggleCategory('SHARED')}
                      onAgentSelect={onAgentSelect}
                      onNewConversation={onNewConversation}
                      favoriteAgentIds={favoriteAgentIdSet}
                      onToggleFavorite={toggleFavoriteAgent}
                    />
                  ) : null}

                  {categorized.chatbot.length > 0 ? (
                    <AgentSection
                      title="CHATBOTS"
                      icon={<Bot className="h-3 w-3" />}
                      agents={categorized.chatbot}
                      agentConversationSummaryMap={agentConversationSummaryMap}
                      isCollapsed={collapsedCategories.CHATBOTS || false}
                      onToggleCollapse={() => toggleCategory('CHATBOTS')}
                      onAgentSelect={onAgentSelect}
                      onNewConversation={onNewConversation}
                      favoriteAgentIds={favoriteAgentIdSet}
                      onToggleFavorite={toggleFavoriteAgent}
                    />
                  ) : null}

                  {categorized.utilities.length > 0 ? (
                    <AgentSection
                      title="UTILITIES"
                      icon={<Wrench className="h-3 w-3" />}
                      agents={categorized.utilities}
                      agentConversationSummaryMap={agentConversationSummaryMap}
                      isCollapsed={collapsedCategories.UTILITIES || false}
                      onToggleCollapse={() => toggleCategory('UTILITIES')}
                      onAgentSelect={onAgentSelect}
                      onNewConversation={onNewConversation}
                      favoriteAgentIds={favoriteAgentIdSet}
                      onToggleFavorite={toggleFavoriteAgent}
                    />
                  ) : null}

                  {categorized.user.length > 0 ? (
                    <AgentSection
                      title="YOUR AGENTS"
                      icon={<User className="h-3 w-3" />}
                      agents={categorized.user}
                      agentConversationSummaryMap={agentConversationSummaryMap}
                      isCollapsed={collapsedCategories.YOUR_AGENTS || false}
                      onToggleCollapse={() => toggleCategory('YOUR_AGENTS')}
                      onAgentSelect={onAgentSelect}
                      onNewConversation={onNewConversation}
                      showOwner
                      favoriteAgentIds={favoriteAgentIdSet}
                      onToggleFavorite={toggleFavoriteAgent}
                    />
                  ) : null}

                  {groupConversations.length > 0 ? (
                    <div>
                      <SectionHeader
                        title="GROUPS"
                        icon={<Users className="h-3 w-3" />}
                        isCollapsed={collapsedCategories.GROUPS || false}
                        onToggleCollapse={() => toggleCategory('GROUPS')}
                      />
                      <div className={cn('overflow-hidden transition-all duration-200 ease-in-out', collapsedCategories.GROUPS ? 'max-h-0 opacity-0' : 'max-h-[9999px] opacity-100')}>
                        <div className="space-y-2">
                          {groupConversations.map((conversation) => (
                            <ConversationCard
                              key={conversation.id}
                              conversation={conversation}
                              asyncJobSummary={asyncJobSummaries[conversation.id] ?? null}
                              isActive={conversation.id === currentConversationId}
                              archived={false}
                              onSelect={onConversationSelect}
                              onTogglePinned={toggleConversationPinned}
                              onToggleArchived={toggleConversationArchived}
                              onHide={hideConversation}
                              onUnhide={unhideConversation}
                              onMarkUnread={markConversationUnread}
                              onManageTags={handleOpenTagManager}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isTagManagerOpen ? (
        <React.Suspense fallback={<LoadingFallback />}>
          <TagManagerModal
            isOpen={isTagManagerOpen}
            onClose={() => {
              setIsTagManagerOpen(false);
              setSelectedConversationId(null);
            }}
            conversation={selectedConversationForTags}
          />
        </React.Suspense>
      ) : null}
    </>
  );
}

function FilterSegmentButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        ui.button.base,
        active ? ui.button.chipActive : ui.button.chip,
        'h-11 min-w-[44px] rounded-xl px-3 text-xs',
      )}
      aria-label={label}
      title={label}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function SectionHeader({
  title,
  icon,
  isCollapsed,
  onToggleCollapse,
  collapsible = true,
}: {
  title: string;
  icon: React.ReactNode;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  collapsible?: boolean;
}) {
  if (!collapsible) {
    return (
      <div className="mb-2 flex items-center gap-2 px-1">
        <div className="text-muted-foreground">{icon}</div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggleCollapse}
      className="-m-1 mb-2 flex min-h-[44px] w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-muted-foreground transition-colors duration-150 hover:bg-background hover:text-foreground"
      aria-expanded={!isCollapsed}
      aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${title}`}
    >
      <div>{isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</div>
      <div>{icon}</div>
      <h3 className="text-xs font-semibold uppercase tracking-wide">{title}</h3>
    </button>
  );
}

function ConversationCard({
  conversation,
  asyncJobSummary,
  isActive,
  archived,
  showingHidden,
  onSelect,
  onTogglePinned,
  onToggleArchived,
  onHide,
  onUnhide,
  onMarkUnread,
  onManageTags,
}: {
  conversation: Conversation;
  asyncJobSummary?: AsyncJobConversationSummary | null;
  isActive: boolean;
  archived: boolean;
  showingHidden?: boolean;
  onSelect: (conversationId: string) => void;
  onTogglePinned: (conversationId: string) => void;
  onToggleArchived: (conversationId: string) => void;
  onHide: (conversationId: string) => void;
  onUnhide?: (conversationId: string) => void;
  onMarkUnread: (conversationId: string) => void;
  onManageTags: (conversation?: Conversation | null) => void;
}) {
  const hasActiveAsyncJob = Boolean(asyncJobSummary?.activeJobCount);
  const preview = hasActiveAsyncJob
    ? asyncJobSummary?.progressMessage || 'Still working on your request'
    : conversation.lastMessage?.content || conversation.summary || 'No messages yet';
  return (
    <div
      role="button"
      aria-label={`Open conversation ${conversation.title || conversation.groupName || 'Untitled conversation'}`}
      tabIndex={0}
      onClick={() => onSelect(conversation.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(conversation.id);
        }
      }}
      className={cn(
        'group relative min-h-[64px] rounded-xl border px-2.5 py-2 text-left transition-colors duration-150',
        isActive
          ? 'border-blue-500/40 bg-blue-500/12 text-foreground-bright shadow-lg shadow-blue-500/10'
          : archived
            ? 'border-border bg-background/60 text-muted-foreground hover:bg-card'
            : hasActiveAsyncJob
              ? 'border-blue-500/25 bg-blue-500/[0.08] text-foreground hover:bg-blue-500/[0.12]'
              : 'border-border bg-card/80 text-foreground hover:bg-background/80',
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className="shrink-0">
          {conversation.isGroup ? (
            <div className={cn('flex h-8 w-8 items-center justify-center rounded-full bg-background text-sm font-semibold text-foreground ring-1 ring-white/10', isActive && 'bg-blue-500/20 text-blue-50')}>
              <Users className="h-3.5 w-3.5" />
            </div>
          ) : (
            <AgentAvatar agent={{ name: conversation.agentName || conversation.title || 'Conversation', avatar: conversation.agentAvatar }} size="sm" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <div className={cn('truncate text-[13px] font-medium leading-tight', archived && !isActive && 'text-muted-foreground')}>
                {conversation.title || conversation.groupName || 'Untitled conversation'}
              </div>
              {conversation.isPinned ? (
                <Pin className={cn('h-2.5 w-2.5 shrink-0', isActive ? 'text-blue-100/80' : archived ? 'text-muted-foreground' : 'text-highlight-foreground')} />
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {hasActiveAsyncJob ? <AsyncJobSidebarBadge count={asyncJobSummary?.activeJobCount ?? 1} isActive={isActive} /> : null}
              {isConversationVisuallyUnread(conversation) ? (
                conversation.isUnread && getConversationUnreadCount(conversation) === 0 ? (
                  <span
                    className="flex h-4 min-w-[16px] items-center justify-center rounded-full border border-blue-500/30 bg-blue-500/15 px-1 text-[10px] font-semibold text-blue-300"
                    aria-label="Marked as unread"
                  >
                    ●
                  </span>
                ) : (
                  <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                    {getConversationUnreadCount(conversation) > 99 ? '99+' : getConversationUnreadCount(conversation)}
                  </span>
                )
              ) : null}
              <span className={cn('text-[10px] tabular-nums', isActive ? 'text-blue-100/80' : 'text-muted-foreground')}>
                {formatConversationLastActivity(conversation) || formatRelativeTime(conversation.lastActivity)}
              </span>
            </div>
          </div>

          <p className={cn('mt-0.5 truncate text-xs leading-snug', isActive ? 'text-blue-50/90' : 'text-muted-foreground')}>
            {truncateText(preview, 60)}
          </p>
        </div>
      </div>

      {(conversation.tags ?? []).length > 0 ? (
        <div className={cn('mt-1.5 flex flex-wrap gap-1', conversation.isGroup ? 'ml-[42px]' : 'ml-[34px]')}>
          {(conversation.tags ?? []).slice(0, 3).map((tag) => (
            <TagBadge key={`${conversation.id}-${tag.id}`} tag={tag} />
          ))}
        </div>
      ) : null}

      <div className="pointer-events-none absolute right-2 top-2 flex items-center gap-1 rounded-xl border border-border bg-card p-1 opacity-0 shadow-2xl shadow-black/30 transition-all duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
        <ActionIconButton
          label={conversation.isPinned ? 'Unpin conversation' : 'Pin conversation'}
          onClick={() => onTogglePinned(conversation.id)}
          active={conversation.isPinned}
          icon={<Pin className="h-3.5 w-3.5" />}
        />
        {/* AC-MU-4: only show mark-unread when not already unread */}
        {!isConversationVisuallyUnread(conversation) ? (
          <ActionIconButton
            label="Mark as unread"
            onClick={() => onMarkUnread(conversation.id)}
            icon={<Mail className="h-3.5 w-3.5" />}
          />
        ) : null}
        <ActionIconButton
          label={conversation.archivedAt ? 'Unarchive conversation' : 'Archive conversation'}
          onClick={() => onToggleArchived(conversation.id)}
          active={Boolean(conversation.archivedAt)}
          icon={<Archive className="h-3.5 w-3.5" />}
        />
        {/* AC-HIDE-2/3: hide only offered on group conversations; unhide in hidden view */}
        {showingHidden && conversation.isHidden && onUnhide ? (
          <ActionIconButton
            label="Unhide conversation"
            onClick={() => onUnhide(conversation.id)}
            active
            icon={<Eye className="h-3.5 w-3.5" />}
          />
        ) : conversation.isGroup && !conversation.isHidden ? (
          <ActionIconButton
            label="Hide from list"
            onClick={() => onHide(conversation.id)}
            icon={<EyeOff className="h-3.5 w-3.5" />}
          />
        ) : null}
        <ActionIconButton
          label="Manage tags"
          onClick={() => onManageTags(conversation)}
          icon={<TagIcon className="h-3.5 w-3.5" />}
        />
      </div>
    </div>
  );
}

function AsyncJobSidebarBadge({
  count,
  isActive,
  compact = false,
}: {
  count: number;
  isActive: boolean;
  compact?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold',
        isActive
          ? 'border-white/15 bg-white/15 text-white/95'
          : 'border-blue-400/25 bg-blue-500/10 text-blue-200',
      )}
      aria-label={count > 1 ? `${count} async jobs running` : 'Async job running'}
      title={count > 1 ? `${count} async jobs running` : 'Async job running'}
    >
      <Loader2 aria-hidden="true" className={cn('h-3 w-3 shrink-0 ally-async-job-spinner', isActive ? 'text-white/90' : 'text-blue-200')} />
      {compact ? 'Working' : count > 1 ? `${count} running` : 'Working'}
    </span>
  );
}

function ActionIconButton({
  label,
  onClick,
  icon,
  active = false,
}: {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={cn(
        ui.button.icon,
        'h-9 w-9 rounded-lg border-0',
        active && 'text-blue-300',
      )}
      aria-label={label}
      title={label}
    >
      {icon}
    </button>
  );
}

function AgentSection({
  title,
  icon,
  agents,
  agentConversationSummaryMap,
  isCollapsed,
  onToggleCollapse,
  onAgentSelect,
  onNewConversation,
  favoriteAgentIds,
  onToggleFavorite,
  showOwner = false,
  collapsible = true,
}: {
  title: string;
  icon: React.ReactNode;
  agents: Agent[];
  agentConversationSummaryMap: Map<string, AgentConversationSummary>;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onAgentSelect: (agentId: string) => void;
  onNewConversation?: (agentId: string) => void;
  favoriteAgentIds?: Set<string>;
  onToggleFavorite?: (agentId: string) => Promise<void>;
  showOwner?: boolean;
  collapsible?: boolean;
}) {
  return (
    <div>
      <SectionHeader
        title={title}
        icon={icon}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
        collapsible={collapsible}
      />
      <div className={cn('overflow-hidden transition-all duration-200 ease-in-out', isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[9999px] opacity-100')}>
        <div className="space-y-0 divide-y divide-border/50">
          {agents.map((agent) => (
            <AgentItem
              key={agent.id}
              agent={agent}
              conversationSummary={agentConversationSummaryMap.get(agent.id) ?? null}
              onAgentSelect={onAgentSelect}
              onNewConversation={onNewConversation}
              isFavorite={favoriteAgentIds?.has(agent.id) ?? false}
              onToggleFavorite={onToggleFavorite}
              showOwner={showOwner}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AgentItemComponent({
  agent,
  conversationSummary,
  onAgentSelect,
  onNewConversation,
  isFavorite,
  onToggleFavorite,
  showOwner,
}: {
  agent: Agent;
  conversationSummary: AgentConversationSummary | null;
  onAgentSelect: (agentId: string) => void;
  onNewConversation?: (agentId: string) => void;
  isFavorite: boolean;
  onToggleFavorite?: (agentId: string) => Promise<void>;
  showOwner?: boolean;
}) {
  const agentStatus = useChatStore((state) => state.agentStatuses[agent.id]);
  const statusValue = agentStatus?.status || agent.healthStatus || agent.status || (agent.isOnline ? 'online' : 'offline');
  const latestConversation = conversationSummary?.latestConversation ?? null;
  const agentLastMessage = (() => {
    const rawLastMessage = (agent as Agent & { lastMessage?: unknown }).lastMessage;
    if (typeof rawLastMessage === 'string') {
      return rawLastMessage;
    }

    if (rawLastMessage && typeof rawLastMessage === 'object' && typeof (rawLastMessage as { content?: unknown }).content === 'string') {
      return (rawLastMessage as { content: string }).content;
    }

    return '';
  })();
  const latestAsyncJobSummary = conversationSummary?.latestAsyncJobSummary ?? null;
  const previewText = truncateText(
    stripPreviewText(latestAsyncJobSummary?.progressMessage || latestConversation?.lastMessage?.content || agentLastMessage || agent.description || 'Online and ready to help'),
    40,
  );
  const isActive = Boolean(conversationSummary?.isActive);
  const totalUnread = conversationSummary?.totalUnread ?? 0;
  const conversationCount = conversationSummary?.conversationCount ?? 0;
  const activeAsyncJobCount = conversationSummary?.activeAsyncJobCount ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onAgentSelect(agent.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onAgentSelect(agent.id);
        }
      }}
      className={cn('group relative flex min-h-[48px] w-full items-center gap-2.5 rounded-xl border border-transparent px-2.5 py-2 text-left transition-colors duration-150', isActive ? 'border-blue-500/30 bg-blue-600/90 text-white shadow-lg shadow-blue-500/10' : 'text-foreground hover:border-border hover:bg-card/90')}
      aria-label={conversationCount > 0 ? `Open most recent conversation with ${agent.name}` : `Start a conversation with ${agent.name}`}
    >
      {onToggleFavorite ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            void onToggleFavorite(agent.id);
          }}
          className={cn(
            'absolute right-2 top-2 rounded-lg p-1.5 transition-opacity duration-150 hover:bg-white/10',
            isFavorite
              ? 'text-amber-300 opacity-100'
              : isActive
                ? 'text-white/70 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                : 'text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
          )}
          title={isFavorite ? `Remove ${agent.name} from favorites` : `Add ${agent.name} to favorites`}
          aria-label={isFavorite ? `Remove ${agent.name} from favorites` : `Add ${agent.name} to favorites`}
        >
          <Star className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      ) : null}

      <div className="relative flex-shrink-0">
        <AgentAvatar agent={{ name: agent.name, avatar: agent.avatar }} size="md" />
        <div className={cn('ally-status-dot absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-[1.5px]', isActive ? 'border-blue-600' : 'border-card', getAgentStatusDotClass(statusValue))} />
      </div>

      <div className="min-w-0 flex-1 pr-7">
        <div className="mb-1 flex items-center gap-2">
          <span className={cn('truncate text-sm font-medium', isActive ? 'text-white' : 'text-foreground')}>{agent.name}</span>

          {conversationCount > 0 ? (
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                isActive ? 'bg-white/15 text-white/90' : 'bg-background text-muted-foreground',
              )}
              title={`${conversationCount} conversation${conversationCount === 1 ? '' : 's'}`}
              aria-label={`${conversationCount} conversation${conversationCount === 1 ? '' : 's'}`}
            >
              {conversationCount > 99 ? '99+' : conversationCount}
            </span>
          ) : null}

          {activeAsyncJobCount > 0 ? <AsyncJobSidebarBadge count={activeAsyncJobCount} isActive={isActive} compact /> : null}
        </div>

        {showOwner && agent.ownerName ? (
          <p className={cn('mb-1 truncate text-xs', isActive ? 'text-white/70' : 'text-muted-foreground')}>
            👤 {agent.ownerName}
          </p>
        ) : null}

        <p className={cn('truncate text-xs leading-relaxed', isActive ? 'text-white/80' : 'text-muted-foreground')}>
          {previewText}
        </p>
      </div>

      {totalUnread > 0 ? (
        <div className="absolute right-10 top-2 flex h-5 min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
          {totalUnread > 99 ? '99+' : totalUnread}
        </div>
      ) : null}

      {onNewConversation ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onNewConversation(agent.id);
          }}
          className={cn('absolute bottom-2 right-2 rounded-lg p-1.5 transition-opacity duration-150 hover:bg-white/10', isActive ? 'text-white/80 opacity-100' : 'text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100')}
          title={`Start a new chat with ${agent.name}`}
          aria-label={`Start a new chat with ${agent.name}`}
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

const AgentItem = React.memo(AgentItemComponent, (prevProps, nextProps) => (
  prevProps.agent === nextProps.agent
  && prevProps.conversationSummary?.latestConversation === nextProps.conversationSummary?.latestConversation
  && prevProps.conversationSummary?.isActive === nextProps.conversationSummary?.isActive
  && prevProps.conversationSummary?.totalUnread === nextProps.conversationSummary?.totalUnread
  && prevProps.conversationSummary?.conversationCount === nextProps.conversationSummary?.conversationCount
  && prevProps.conversationSummary?.activeAsyncJobCount === nextProps.conversationSummary?.activeAsyncJobCount
  && prevProps.conversationSummary?.latestAsyncJobSummary?.updatedAt === nextProps.conversationSummary?.latestAsyncJobSummary?.updatedAt
  && prevProps.conversationSummary?.latestAsyncJobSummary?.progressMessage === nextProps.conversationSummary?.latestAsyncJobSummary?.progressMessage
  && prevProps.onAgentSelect === nextProps.onAgentSelect
  && prevProps.onNewConversation === nextProps.onNewConversation
  && prevProps.isFavorite === nextProps.isFavorite
  && prevProps.onToggleFavorite === nextProps.onToggleFavorite
  && prevProps.showOwner === nextProps.showOwner
));

export default Sidebar;
